// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/sortEvents.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// src/features/home/sortEvents.js
//
// Ordering for the home list, extracted from app/tabs/invitations.js so it can
// be tested and shared with the web app.
//
// The product rule, unchanged from the original:
//   1. Events still deciding their date (an open date poll) come first — they
//      need the user's input, so they are the most actionable thing on screen.
//   2. Then upcoming events, soonest first.
//   3. Then past events, most recent first.
//
// Fixes carried in during extraction:
//   * The original compared dates with `dateA - dateB` on Date objects inside a
//     sort comparator. That works, but an unparseable event_date produced NaN,
//     and a NaN comparator return makes Array.prototype.sort's result
//     implementation-defined — rows genuinely shuffled between renders. Invalid
//     dates are now normalised to a sentinel so ordering is total and stable.
//   * `new Date('9999-12-31')` was used as the "no date" sentinel for upcoming
//     and `'1970-01-01'` for past. Those are kept, but computed once rather
//     than parsed per comparison.

const NO_DATE_UPCOMING = 253370764800000; // 9999-12-31, sorts last among upcoming
const NO_DATE_PAST = 0;                   // 1970-01-01, sorts last among past

/**
 * '2026-10-05' as LOCAL midnight of that calendar day. `new Date()` reads a
 * bare date as UTC midnight - the evening before in the Americas - so US
 * users saw events a day early, and an event today counted as past. (Kept
 * here rather than imported from eventDateTime.js: shared modules stay
 * import-free so their tests can load them.)
 */
function localDate(value) {
  const m = typeof value === 'string' && /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
}

/** Milliseconds for an event's date, or the supplied fallback when unusable. */
function eventTime(event, fallback) {
  const raw = event?.event_date;
  if (!raw) return fallback;
  const t = localDate(raw).getTime();
  return Number.isFinite(t) ? t : fallback;
}

/** True while an event's date is still being voted on. */
export function isOpenDatePoll(event) {
  return !!event?.date_time_poll_enabled && !event?.date_time_poll_selected;
}

/**
 * Splits into upcoming and past relative to the start of `now`'s day, so an
 * event happening later today still counts as upcoming.
 */
export function partitionByTime(events, now = new Date()) {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const cutoff = startOfToday.getTime();

  const upcoming = [];
  const past = [];

  for (const event of Array.isArray(events) ? events : []) {
    if (!event) continue;
    // An undecided date is always upcoming — it has not happened by definition.
    if (isOpenDatePoll(event)) { upcoming.push(event); continue; }
    (eventTime(event, NO_DATE_UPCOMING) >= cutoff ? upcoming : past).push(event);
  }

  return { upcoming, past };
}

/**
 * The ordered home list: open polls, then upcoming ascending, then past
 * descending. Never throws on malformed rows.
 */
export function sortForHome(events, now = new Date()) {
  const { upcoming, past } = partitionByTime(events, now);

  upcoming.sort((a, b) => {
    const aPoll = isOpenDatePoll(a);
    const bPoll = isOpenDatePoll(b);
    if (aPoll !== bPoll) return aPoll ? -1 : 1;
    return eventTime(a, NO_DATE_UPCOMING) - eventTime(b, NO_DATE_UPCOMING);
  });

  past.sort((a, b) => eventTime(b, NO_DATE_PAST) - eventTime(a, NO_DATE_PAST));

  return [...upcoming, ...past];
}

/**
 * Groups the ordered list into the sections the home screen renders. Empty
 * sections are omitted so the screen never draws a header with nothing under it
 * — a small thing the old flat list got wrong.
 */
export function groupForHome(events, now = new Date()) {
  const { upcoming } = partitionByTime(events, now);
  const ordered = sortForHome(events, now);
  const upcomingIds = new Set(upcoming.map((e) => e.id));

  const sections = [];
  const upcomingOrdered = ordered.filter((e) => upcomingIds.has(e.id));
  const pastOrdered = ordered.filter((e) => !upcomingIds.has(e.id));

  if (upcomingOrdered.length) sections.push({ key: 'upcoming', title: 'Upcoming', data: upcomingOrdered });
  if (pastOrdered.length)     sections.push({ key: 'past',     title: 'Past',     data: pastOrdered });
  return sections;
}
